import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../../core/api/client';
import { FaKey, FaStore, FaUser, FaSave, FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './SasheSettings.css';

function SasheSettings() {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showPassword, setShowPassword] = useState({});
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await api.get('/sashe-credentials');
      setCredentials(response.data);
      
      // Initialize edited data
      const edited = {};
      response.data.forEach(cred => {
        edited[cred.id] = {
          username: cred.username,
          password: '',
          is_active: cred.is_active
        };
      });
      setEditedData(edited);
    } catch (error) {
      console.error('Error fetching credentials:', error);
      toast.error('Chyba pri načítaní prihlasovacích údajov');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (credId, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [credId]: {
        ...prev[credId],
        [field]: value
      }
    }));
  };

  const handleSave = async (credId) => {
    setSaving(prev => ({ ...prev, [credId]: true }));
    
    try {
      const data = editedData[credId];
      
      // Only send password if it was changed (not empty)
      const updateData = {
        username: data.username,
        is_active: data.is_active
      };
      
      if (data.password && data.password.trim() !== '') {
        updateData.password = data.password;
      }
      
      await api.put(`/sashe-credentials/${credId}`, updateData);
      
      toast.success('Prihlasovacie údaje boli úspešne aktualizované');
      
      // Clear password field after successful save
      setEditedData(prev => ({
        ...prev,
        [credId]: {
          ...prev[credId],
          password: ''
        }
      }));
      
      // Refresh credentials
      fetchCredentials();
    } catch (error) {
      console.error('Error updating credentials:', error);
      toast.error('Chyba pri ukladaní prihlasovacích údajov');
    } finally {
      setSaving(prev => ({ ...prev, [credId]: false }));
    }
  };

  const getEshopDisplayName = (eshop) => {
    const names = {
      'monama': 'Monama',
      'monama_svadobka': 'Monama Svadobka'
    };
    return names[eshop] || eshop;
  };

  const togglePasswordVisibility = (credId) => {
    setShowPassword(prev => ({
      ...prev,
      [credId]: !prev[credId]
    }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Načítavam nastavenia...</p>
      </div>
    );
  }

  return (
    <div className="sashe-settings-container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="settings-header">
          <h1>
            <FaKey className="header-icon" />
            Nastavenia Sashe prihlásenia
          </h1>
          <p className="settings-description">
            Tu môžete spravovať prihlasovacie údaje pre automatizáciu Sashe e-shopov
          </p>
        </div>

        <div className="credentials-grid">
          {credentials.map(cred => (
            <motion.div
              key={cred.id}
              className={`credential-card ${!cred.is_active ? 'inactive' : ''}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="card-header">
                <FaStore className="shop-icon" />
                <h2>{getEshopDisplayName(cred.eshop)}</h2>
                <div className={`status-badge ${cred.is_active ? 'active' : 'inactive'}`}>
                  {cred.is_active ? 'Aktívny' : 'Neaktívny'}
                </div>
              </div>

              <div className="card-body">
                <div className="form-group">
                  <label>
                    <FaUser className="field-icon" />
                    Používateľské meno:
                  </label>
                  <input
                    type="text"
                    value={editedData[cred.id]?.username || ''}
                    onChange={(e) => handleInputChange(cred.id, 'username', e.target.value)}
                    className="form-input"
                    placeholder="Zadajte používateľské meno"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FaKey className="field-icon" />
                    Heslo:
                  </label>
                  <div className="password-input-group">
                    <input
                      type={showPassword[cred.id] ? 'text' : 'password'}
                      value={editedData[cred.id]?.password || ''}
                      onChange={(e) => handleInputChange(cred.id, 'password', e.target.value)}
                      className="form-input"
                      placeholder="Nechajte prázdne ak nechcete zmeniť"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(cred.id)}
                      className="password-toggle"
                    >
                      {showPassword[cred.id] ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedData[cred.id]?.is_active || false}
                      onChange={(e) => handleInputChange(cred.id, 'is_active', e.target.checked)}
                    />
                    <span>Aktívny e-shop</span>
                  </label>
                </div>

                {cred.updated_at && (
                  <div className="last-updated">
                    Naposledy aktualizované: {new Date(cred.updated_at).toLocaleString('sk-SK')}
                  </div>
                )}

                <button
                  onClick={() => handleSave(cred.id)}
                  disabled={saving[cred.id]}
                  className="save-button"
                >
                  {saving[cred.id] ? (
                    <>
                      <div className="button-spinner"></div>
                      Ukladám...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Uložiť zmeny
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="info-box">
          <h3>ℹ️ Informácie</h3>
          <ul>
            <li>Tieto prihlasovacie údaje sa používajú pre automatické spracovanie produktov na Sashe.sk</li>
            <li>CRON úloha beží každý deň o 2:00 ráno a používa tieto údaje</li>
            <li>Ak je e-shop nastavený ako neaktívny, jeho produkty nebudú spracované</li>
            <li>Heslá sú bezpečne uložené v databáze</li>
            <li>Pri zmene hesla zadajte nové heslo, inak nechajte pole prázdne</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}

export default SasheSettings;